import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, MicOff, Play, Pause, Trash2, Check, Loader2, AlertCircle } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const VoiceRecorder = ({ 
  sessionId, 
  projectId = "factoryhype",
  maxDuration = 30, 
  promptText,
  onComplete,
  onSkip 
}) => {
  const [status, setStatus] = useState("idle"); // idle, recording, recorded, playing, uploading, error
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [permissionDenied, setPermissionDenied] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioRef = useRef(null);
  const streamRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, [audioUrl]);

  // Request microphone permission
  const requestMicrophonePermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        } 
      });
      streamRef.current = stream;
      return stream;
    } catch (err) {
      console.error("Microphone permission denied:", err);
      setPermissionDenied(true);
      setErrorMessage("Bitte erlaube den Zugriff auf dein Mikrofon, um eine Sprachnachricht aufzunehmen.");
      return null;
    }
  };

  // Start recording
  const startRecording = async () => {
    setErrorMessage(null);
    setPermissionDenied(false);
    
    const stream = await requestMicrophonePermission();
    if (!stream) return;

    audioChunksRef.current = [];
    
    // Check for supported MIME types
    const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
      ? "audio/webm;codecs=opus"
      : MediaRecorder.isTypeSupported("audio/webm")
      ? "audio/webm"
      : "audio/mp4";

    try {
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        setAudioBlob(audioBlob);
        setStatus("uploading");
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
        
        // Auto-upload immediately after recording stops
        uploadRecordingWithBlob(audioBlob, mimeType);
      };

      mediaRecorder.onerror = (event) => {
        console.error("MediaRecorder error:", event);
        setErrorMessage("Aufnahme fehlgeschlagen. Bitte versuche es erneut.");
        setStatus("error");
        stream.getTracks().forEach(track => track.stop());
      };

      // Start recording
      mediaRecorder.start(1000); // Collect data every second
      setStatus("recording");
      setRecordingTime(0);

      // Timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= maxDuration - 1) {
            stopRecording();
            return maxDuration;
          }
          return prev + 1;
        });
      }, 1000);

    } catch (err) {
      console.error("Error starting recording:", err);
      setErrorMessage("Aufnahme konnte nicht gestartet werden. Bitte prüfe dein Mikrofon.");
      setStatus("error");
      stream.getTracks().forEach(track => track.stop());
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
  };

  // Play recorded audio
  const playAudio = () => {
    if (audioRef.current) {
      audioRef.current.play();
      setStatus("playing");
    }
  };

  // Pause audio
  const pauseAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setStatus("recorded");
    }
  };

  // Delete recording and start over
  const deleteRecording = () => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioBlob(null);
    setAudioUrl(null);
    setRecordingTime(0);
    setStatus("idle");
    setErrorMessage(null);
  };

  // Upload recording with blob directly (for auto-upload)
  const uploadRecordingWithBlob = async (blob, mimeType) => {
    setStatus("uploading");
    setErrorMessage(null);

    try {
      const formData = new FormData();
      const extension = mimeType.includes("webm") ? "webm" : "mp4";
      formData.append("file", blob, `voice_message.${extension}`);

      const response = await axios.post(
        `${API}/voice/upload?session_id=${sessionId}&project_id=${projectId}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 60000
        }
      );

      if (response.data.success) {
        onComplete?.({
          audioPath: response.data.audio_path,
          transcript: response.data.transcript,
          duration: recordingTime
        });
      } else {
        throw new Error(response.data.error || "Upload fehlgeschlagen");
      }
    } catch (err) {
      console.error("Upload error:", err);
      setErrorMessage("Upload fehlgeschlagen. Bitte versuche es erneut.");
      setStatus("idle");
    }
  };

  // Upload recording (legacy, for button click)
  const uploadRecording = async () => {
    if (!audioBlob) return;

    setStatus("uploading");
    setErrorMessage(null);

    try {
      const formData = new FormData();
      const extension = audioBlob.type.includes("webm") ? "webm" : "mp4";
      formData.append("file", audioBlob, `voice_message.${extension}`);

      const response = await axios.post(
        `${API}/voice/upload?session_id=${sessionId}&project_id=${projectId}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 60000 // 60 second timeout for upload
        }
      );

      if (response.data.success) {
        onComplete?.({
          audioPath: response.data.audio_path,
          transcript: response.data.transcript,
          duration: recordingTime
        });
      } else {
        throw new Error(response.data.error || "Upload fehlgeschlagen");
      }
    } catch (err) {
      console.error("Upload error:", err);
      setErrorMessage("Upload fehlgeschlagen. Bitte versuche es erneut.");
      setStatus("recorded"); // Go back to recorded state so user can retry
    }
  };

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className="w-full max-w-md mx-auto" data-testid="voice-recorder">
      {/* Prompt Text */}
      <div className="mb-6 p-4 bg-emerald-50 rounded-xl border border-emerald-200">
        <p className="text-slate-700 text-sm leading-relaxed">
          {promptText || "Bitte nimm eine kurze Sprachnachricht auf."}
        </p>
      </div>

      {/* Error Message */}
      <AnimatePresence>
        {errorMessage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2"
          >
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-700 text-sm">{errorMessage}</p>
              {permissionDenied && (
                <button
                  onClick={startRecording}
                  className="mt-2 text-sm text-red-600 underline hover:text-red-800"
                >
                  Erneut versuchen
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Recording Interface */}
      <div className="bg-white rounded-2xl shadow-lg p-6 border border-slate-200">
        
        {/* Audio Element (hidden) */}
        {audioUrl && (
          <audio
            ref={audioRef}
            src={audioUrl}
            onEnded={() => setStatus("recorded")}
            className="hidden"
          />
        )}

        {/* Status Display */}
        <div className="text-center mb-6">
          {status === "idle" && (
            <p className="text-slate-600">Klicke auf das Mikrofon, um zu starten</p>
          )}
          {status === "recording" && (
            <div className="flex items-center justify-center gap-2">
              <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></span>
              <span className="text-red-600 font-medium">Aufnahme läuft...</span>
            </div>
          )}
          {status === "recorded" && (
            <p className="text-emerald-600 font-medium">Aufnahme fertig!</p>
          )}
          {status === "playing" && (
            <p className="text-blue-600 font-medium">Wiedergabe...</p>
          )}
          {status === "uploading" && (
            <div className="flex items-center justify-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin text-emerald-600" />
              <span className="text-emerald-600 font-medium">Wird hochgeladen...</span>
            </div>
          )}
        </div>

        {/* Timer / Duration */}
        <div className="text-center mb-6">
          <span className="text-3xl font-mono font-bold text-slate-800">
            {formatTime(recordingTime)}
          </span>
          <span className="text-slate-400 text-lg"> / {formatTime(maxDuration)}</span>
        </div>

        {/* Progress Bar */}
        <div className="w-full h-2 bg-slate-100 rounded-full mb-6 overflow-hidden">
          <motion.div
            className={`h-full rounded-full ${
              status === "recording" ? "bg-red-500" : "bg-emerald-500"
            }`}
            initial={{ width: 0 }}
            animate={{ width: `${(recordingTime / maxDuration) * 100}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>

        {/* Control Buttons */}
        <div className="flex items-center justify-center gap-4">
          
          {/* Idle State - Start Recording */}
          {status === "idle" && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={startRecording}
              className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 text-white flex items-center justify-center shadow-lg hover:shadow-xl transition-shadow"
              data-testid="start-recording-btn"
            >
              <Mic className="w-10 h-10" />
            </motion.button>
          )}

          {/* Recording State - Stop (will auto-upload) */}
          {status === "recording" && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={stopRecording}
              className="w-20 h-20 rounded-full bg-red-500 text-white flex items-center justify-center shadow-lg hover:shadow-xl transition-shadow animate-pulse"
              data-testid="stop-recording-btn"
            >
              <MicOff className="w-10 h-10" />
            </motion.button>
          )}

          {/* Uploading State */}
          {status === "uploading" && (
            <div className="flex flex-col items-center gap-3">
              <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center">
                <Loader2 className="w-10 h-10 animate-spin text-emerald-600" />
              </div>
              <span className="text-emerald-600 text-sm">Wird verarbeitet...</span>
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className="mt-6 text-center text-sm text-slate-500">
          {status === "idle" && "Tippe auf das Mikrofon um zu starten"}
          {status === "recording" && "Tippe zum Beenden"}
          {status === "uploading" && ""}
        </div>
      </div>
    </div>
  );
};

export default VoiceRecorder;
